import logError from "commons/utils/logError";
import { Service } from "services/Service";
import NotificationApiService from "services/notification/NotificationApiService";
import { getToken, parseJWT } from "services/Auth";

const PUSH_REGISTERED_AT_KEY = "@beedoo_push_registered_at";
const PUSH_REGISTERED_ID_KEY = "@beedoo_push_registered_id";
const PUSH_TOUCHED_AT_KEY = "@beedoo_push_touched_at";
const PUSH_COOLDOWN_MS = 10 * 60 * 1000;
const PUSH_HEARTBEAT_MS = 5 * 60 * 1000;
const MAX_ATTEMPTS = 10;
const BASE_DELAY_MS = 400;

class PushDeviceService extends Service {
  constructor() {
    super(NotificationApiService);
    this._heartbeatTimer = null;
  }

  _delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  _getUserIdFromToken() {
    const payload = parseJWT(getToken());
    if (!payload || payload.id == null) {
      return null;
    }
    return String(payload.id);
  }

  _getLastRegisteredId() {
    try {
      return localStorage.getItem(PUSH_REGISTERED_ID_KEY);
    } catch (err) {
      logError("PushDeviceService.getLastRegisteredId", err);
      return null;
    }
  }

  _isWithinCooldown(onesignalSubscriptionId) {
    try {
      if (onesignalSubscriptionId !== this._getLastRegisteredId()) {
        return false;
      }
      const raw = localStorage.getItem(PUSH_REGISTERED_AT_KEY);
      if (!raw) {
        return false;
      }
      return Date.now() - Number(raw) < PUSH_COOLDOWN_MS;
    } catch (err) {
      logError("PushDeviceService.isWithinCooldown", err);
      return false;
    }
  }

  _isTouchDue() {
    try {
      const raw = localStorage.getItem(PUSH_TOUCHED_AT_KEY);
      if (!raw) {
        return true;
      }
      return Date.now() - Number(raw) >= PUSH_HEARTBEAT_MS;
    } catch (err) {
      logError("PushDeviceService.isTouchDue", err);
      return true;
    }
  }

  _persistTouchTimestamp() {
    try {
      localStorage.setItem(PUSH_TOUCHED_AT_KEY, String(Date.now()));
    } catch (err) {
      logError("PushDeviceService.persistTouchTimestamp", err);
    }
  }

  resetRegistrationState() {
    try {
      localStorage.removeItem(PUSH_REGISTERED_AT_KEY);
      localStorage.removeItem(PUSH_REGISTERED_ID_KEY);
      localStorage.removeItem(PUSH_TOUCHED_AT_KEY);
    } catch (err) {
      logError("PushDeviceService.resetRegistrationState", err);
    }
  }

  async register(onesignalSubscriptionId, platform) {
    if (!onesignalSubscriptionId || typeof onesignalSubscriptionId !== "string") {
      return;
    }

    const userId = this._getUserIdFromToken();
    if (!userId) {
      return;
    }

    if (this._isWithinCooldown(onesignalSubscriptionId)) {
      return;
    }

    const payload = {
      onesignal_subscription_id: onesignalSubscriptionId,
      platform: platform || "unknown",
    };

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
      try {
        const result = await this.postWaiting("/api/push/device", payload);
        if (result === undefined) {
          throw new Error("PushDeviceService: register request failed");
        }
        try {
          localStorage.setItem(PUSH_REGISTERED_AT_KEY, String(Date.now()));
          localStorage.setItem(PUSH_REGISTERED_ID_KEY, onesignalSubscriptionId);
          this._persistTouchTimestamp();
        } catch (err) {
          logError("PushDeviceService.persistRegistrationTimestamp", err);
        }
        return;
      } catch (err) {
        logError(`PushDeviceService.register.attempt${attempt}`, err);
        if (attempt < MAX_ATTEMPTS) {
          const backoff = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), 8000);
          await this._delay(backoff);
        }
      }
    }
  }

  async _performTouch() {
    const id = typeof window !== "undefined" ? window.__BEEDOO_ONESIGNAL_ID__ : null;
    if (!id || typeof id !== "string") {
      return;
    }
    const result = await this.postWaiting("/api/push/device", {
      onesignal_subscription_id: id,
      platform: window.__BEEDOO_ONESIGNAL_PLATFORM__ || "unknown",
    });
    if (result === undefined) {
      throw new Error("PushDeviceService: touch request failed");
    }
    this._persistTouchTimestamp();
  }

  async touchIfDue() {
    if (!this._isTouchDue()) {
      return;
    }
    try {
      await this._performTouch();
    } catch (err) {
      logError("PushDeviceService.touchIfDue", err);
    }
  }

  async touch() {
    await this.touchIfDue();
  }

  startHeartbeat() {
    if (this._heartbeatTimer) {
      return;
    }
    this.touchIfDue().catch((err) =>
      logError("PushDeviceService.startHeartbeat", err)
    );
    this._heartbeatTimer = setInterval(() => {
      this.touchIfDue().catch((err) =>
        logError("PushDeviceService.startHeartbeat.interval", err)
      );
    }, PUSH_HEARTBEAT_MS);
  }

  stopHeartbeat() {
    if (!this._heartbeatTimer) {
      return;
    }
    clearInterval(this._heartbeatTimer);
    this._heartbeatTimer = null;
  }

  async unregister(onesignalSubscriptionId) {
    if (!onesignalSubscriptionId || typeof onesignalSubscriptionId !== "string") {
      return;
    }

    const result = await this.delete("/api/push/device", {
      data: { onesignal_subscription_id: onesignalSubscriptionId },
    });
    if (result?.error) {
      logError("PushDeviceService.unregister", result.error);
    }

    this.resetRegistrationState();
  }
}

export default new PushDeviceService();
